/* eslint-disable react/display-name */
import PropTypes from 'prop-types';
import { useState, useRef, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { Form, Spinner } from 'react-bootstrap';

import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';
import Icon from '_components/atomic/Icon';

const { colors, fontSizes, spaces } = theme;

const StyledGroup = styled(Form.Group)`
  position: relative;
`;

const StyledControl = styled(
  forwardRef(({ processing, ...props }, ref) => (
    <Form.Control ref={ref} {...props} />
  ))
)`
  min-height: 52px;

  padding-top: ${spaces.small}px;
  padding-bottom: ${spaces.small}px;
  padding-left: ${spaces.large4X}px;
  padding-right: ${spaces.regular}px;

  border: 1px solid ${colors.gray[200]};
  border-radius: 8px;
  background-color: ${colors.white};

  font-family: 'Aeonik';
  font-size: ${fontSizes.medium}px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  color: ${colors.gray[800]};
  caret-color: ${colors.indigo[500]};

  &::placeholder {
    color: ${colors.gray[500]};
  }

  &:focus {
    border-color: ${colors.indigo[500]};
    box-shadow: none;
  }

  &:disabled {
    background-color: ${colors.gray[100]};
  }

  &.form-control.is-valid {
    padding-right: ${spaces.regular}px;
    border-color: ${colors.gray[200]};
    background-image: none;

    &:focus {
      border-color: ${colors.indigo[500]};
      box-shadow: none;
    }
  }

  &.form-control.is-invalid {
    padding-right: ${spaces.regular}px;
    border-color: ${colors.orange[600]};
    background-image: none;

    &:focus {
      border-color: ${colors.indigo[500]};
      box-shadow: none;
    }
  }

  ${({ processing }) =>
    processing &&
    css`
      padding-right: ${spaces.large6X}px;
    `};

  &::-webkit-file-upload-button {
    display: none;
  }

  &::-webkit-autofill,
  &::-webkit-contacts-auto-fill-button,
  &::-webkit-credentials-auto-fill-button {
    visibility: hidden;
  }
`;

const StyledIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  height: 100%;
  left: 12px;
`;

const StyledSpinner = styled(Spinner)`
  position: absolute;
  top: ${(52 - 32) / 2}px;
  right: ${spaces.smallX}px;

  &.text-custom {
    color: ${colors.gray[500]};
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const SearchInput = forwardRef(
  ({ name, value, onChange, onInputChange, ...restProps }, ref) => {
    const timeoutRef = useRef(null);
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
      onInputChange && onInputChange(e);
      clearTimeout(timeoutRef.current);
      setLoading(true);
      timeoutRef.current = setTimeout(() => {
        onChange && onChange(e);
        clearTimeout(timeoutRef.current);
        setLoading(false);
      }, 1000);
    };

    return (
      <StyledGroup>
        <StyledIconContainer>
          <Icon
            alias={ICONS_ALIASES.searchNormal}
            color={theme.colors.gray[500]}
          />
        </StyledIconContainer>
        <StyledControl
          {...restProps}
          ref={ref}
          processing={loading}
          value={value}
          onChange={handleChange}
          data-hj-allow
        />
        {loading && <StyledSpinner animation="border" variant="custom" />}
      </StyledGroup>
    );
  }
);

SearchInput.propTypes = {
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onInputChange: PropTypes.func,
};

export default SearchInput;
